// RUTA: src/components/GlobalErrorBoundary.tsx
//
// Error Boundary global que captura cualquier crash de React en el
// árbol de componentes. Muestra una pantalla amigable con la opción
// de crear un ticket de soporte automáticamente con el stack trace
// + screenshot.
//
// Montado al nivel del App.tsx → cualquier crash en cualquier ruta
// (cliente, coach, admin) cae aquí.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, LifeBuoy, Loader2, Home, Trash2 } from 'lucide-react';
import { createTicketAuto } from '@/lib/createTicket';
import { TicketCreatedDialog } from './support/TicketCreatedDialog';
import {
  performCleanReload,
  isConnectionHealthy,
  hasReachedCleanReloadCap,
  getCleanReloadAttempts,
  MAX_CLEAN_RELOAD_ATTEMPTS,
} from '@/lib/cleanReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  ticketCreating: boolean;
  ticketNumber: string | null;
  confirmOpen: boolean;
  cleanReloadChecking: boolean;
  cleanReloadRunning: boolean;
  cleanReloadError: string | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    ticketCreating: false,
    ticketNumber: null,
    confirmOpen: false,
    cleanReloadChecking: false,
    cleanReloadRunning: false,
    cleanReloadError: null,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    // Log para que aparezca en consola del browser y en cualquier
    // monitoreo externo (Sentry, Vercel Logs, etc.) que se conecte
    console.error('[GlobalErrorBoundary]', error, errorInfo);
  }

  reset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      ticketCreating: false,
      ticketNumber: null,
      confirmOpen: false,
      cleanReloadChecking: false,
      cleanReloadRunning: false,
      cleanReloadError: null,
    });
  };

  cleanReload = async () => {
    if (this.state.cleanReloadChecking || this.state.cleanReloadRunning) return;
    this.setState({ cleanReloadChecking: true, cleanReloadError: null });
    try {
      const healthy = await isConnectionHealthy();
      if (!healthy) {
        this.setState({
          cleanReloadChecking: false,
          cleanReloadError:
            'Tu conexión parece inestable. Mejor inténtalo cuando tengas mejor señal — sin internet la recarga limpia te dejaría peor.',
        });
        return;
      }
      this.setState({ cleanReloadChecking: false, cleanReloadRunning: true });
      await performCleanReload();
      // performCleanReload llama window.location.replace → no llegamos acá
    } catch {
      this.setState({
        cleanReloadChecking: false,
        cleanReloadRunning: false,
        cleanReloadError: 'No pudimos hacer la recarga limpia. Intenta crear un ticket.',
      });
    }
  };

  goHome = () => {
    window.location.href = '/';
  };

  reload = () => {
    window.location.reload();
  };

  createTicket = async () => {
    if (this.state.ticketCreating) return;
    this.setState({ ticketCreating: true });
    const { error, errorInfo } = this.state;
    const attempts = getCleanReloadAttempts();
    const reachedCap = attempts >= MAX_CLEAN_RELOAD_ATTEMPTS;

    // Si llegamos al cap de recargas limpias, este crash es CRÍTICO:
    // el cliente ya intentó todo lo que estaba en sus manos. El title
    // y descripción reflejan eso para que la auto-clasificación
    // ITIL lo marque como P1 (createTicketAuto detecta `?_clr=3+` en
    // la URL y asigna P1 automáticamente, pero el title también lo
    // hace obvio al admin que abre el email).
    const result = await createTicketAuto({
      title: reachedCap
        ? `[P1 Crítico] Crash persistente tras ${attempts} recargas limpias: ${error?.message?.slice(0, 60) || 'sin mensaje'}`
        : `[Crash] ${error?.message?.slice(0, 80) || 'Error desconocido en React'}`,
      description: reachedCap
        ? `El cliente intentó recargar limpio ${attempts} veces y la app sigue crasheando. Esto requiere intervención del equipo de inmediato.\n\n**Mensaje:** ${error?.message || '(sin mensaje)'}`
        : `Crash de React capturado por el Error Boundary global.\n\n**Mensaje:** ${error?.message || '(sin mensaje)'}\n\nLa pantalla se quedó en blanco para el usuario.`,
      category: 'bug',
      captureScreenshot: false, // la pantalla está rota — no tiene sentido capturar
      extra: {
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        url: window.location.href,
        cleanReloadAttempts: attempts,
      },
    });
    this.setState({
      ticketCreating: false,
      ticketNumber: result?.ticket_number || null,
      confirmOpen: !!result,
    });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const reachedCap = hasReachedCleanReloadCap();
    const attempts = getCleanReloadAttempts();

    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className={`max-w-md w-full rounded-2xl border-2 bg-card p-6 shadow-lg ${reachedCap ? 'border-destructive/60' : 'border-destructive/40'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <div>
              <h1 className="text-base font-black uppercase tracking-wide text-foreground">
                {reachedCap ? 'Esto requiere nuestra ayuda' : 'Algo no se cargó bien'}
              </h1>
              <p className="text-xs text-muted-foreground">
                {reachedCap
                  ? `Ya intentamos recargar ${attempts} veces sin éxito`
                  : 'Tuvimos un problema mostrando la app'
                }
              </p>
            </div>
          </div>

          {reachedCap ? (
            // ── Llegamos al cap: solo crear ticket urgente ──
            <>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                Esto significa que el problema NO es algo que puedas resolver tú —
                requiere que lo revisemos nosotros. Crea un ticket y te respondemos
                lo antes posible.
              </p>

              <button
                onClick={this.createTicket}
                disabled={this.state.ticketCreating || this.state.ticketNumber !== null}
                className="w-full mb-3 flex items-center justify-center gap-2 py-3 rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground font-black text-sm uppercase tracking-wider disabled:opacity-60 transition-colors min-h-[48px]"
              >
                {this.state.ticketCreating
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando reporte…</>
                  : this.state.ticketNumber
                    ? <>✓ Reporte {this.state.ticketNumber} creado</>
                    : <><LifeBuoy className="w-4 h-4" /> Crear ticket urgente</>
                }
              </button>

              <p className="text-[10px] text-muted-foreground text-center leading-relaxed mb-3">
                Tu reporte incluye los detalles técnicos automáticamente.
                Recibirás respuesta lo antes posible.
              </p>

              <button
                onClick={this.goHome}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-muted-foreground hover:text-foreground font-medium text-[11px] uppercase tracking-wider transition-colors"
              >
                <Home className="w-3.5 h-3.5" />
                Volver al inicio
              </button>
            </>
          ) : (
            // ── Camino normal: recargar / recarga limpia / reportar ──
            <>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                Lo más rápido es recargar — muchas veces se resuelve solo.
                Si el problema sigue, prueba una recarga limpia o repórtalo.
              </p>

              {/* 1. Recargar normal — primer intento, casi siempre funciona */}
              <button
                onClick={this.reload}
                className="w-full mb-2 flex items-center justify-center gap-2 py-3 rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground font-black text-sm uppercase tracking-wider transition-colors min-h-[48px]"
              >
                <RefreshCw className="w-4 h-4" />
                Recargar la app
              </button>

              {/* 2. Recarga limpia — segundo intento, borra cache + SW.
                   Solo si pasa la verificación de conexión, para no
                   dejar al cliente sin caché Y sin red. */}
              <button
                onClick={this.cleanReload}
                disabled={this.state.cleanReloadChecking || this.state.cleanReloadRunning}
                className="w-full mb-2 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-accent/40 bg-accent/5 hover:bg-accent/10 text-foreground font-bold text-xs uppercase tracking-wider disabled:opacity-60 transition-colors"
              >
                {this.state.cleanReloadChecking
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Verificando conexión…</>
                  : this.state.cleanReloadRunning
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Limpiando…</>
                    : <><Trash2 className="w-4 h-4" /> Probar recarga limpia
                        {attempts > 0 && ` (${attempts + 1}/${MAX_CLEAN_RELOAD_ATTEMPTS})`}
                      </>
                }
              </button>

              {this.state.cleanReloadError && (
                <p className="text-[11px] text-destructive bg-destructive/10 rounded-lg p-2.5 leading-relaxed mb-2">
                  {this.state.cleanReloadError}
                </p>
              )}

              {/* 3. Reportar — tercera opción, si nada más funciona */}
              <button
                onClick={this.createTicket}
                disabled={this.state.ticketCreating || this.state.ticketNumber !== null}
                className="w-full mb-3 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-border hover:bg-secondary text-foreground font-bold text-[11px] uppercase tracking-wider disabled:opacity-60 transition-colors"
              >
                {this.state.ticketCreating
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando reporte…</>
                  : this.state.ticketNumber
                    ? <>✓ Reporte {this.state.ticketNumber} creado — te avisamos pronto</>
                    : <><LifeBuoy className="w-4 h-4" /> Reportar a soporte</>
                }
              </button>

              <button
                onClick={this.goHome}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-muted-foreground hover:text-foreground font-medium text-[11px] uppercase tracking-wider transition-colors"
              >
                <Home className="w-3.5 h-3.5" />
                Volver al inicio
              </button>
            </>
          )}

          {/* Mensaje técnico oculto por default — solo expande si el
           *  usuario quiere verlo. La mayoría no le interesa el stack
           *  trace; al admin sí (vía el ticket creado). */}
          {this.state.error?.message && (
            <details className="mt-4 p-3 rounded-lg bg-secondary/40 border border-border/60">
              <summary className="text-[10px] font-black uppercase tracking-widest text-muted-foreground cursor-pointer">
                Detalles técnicos (para soporte)
              </summary>
              <code className="block mt-2 text-[11px] text-foreground break-words font-mono">
                {this.state.error.message}
              </code>
            </details>
          )}
        </div>

        <TicketCreatedDialog
          open={this.state.confirmOpen}
          onOpenChange={(o) => this.setState({ confirmOpen: o })}
          ticketNumber={this.state.ticketNumber}
        />
      </div>
    );
  }
}
